import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, tokenStore } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import { formatDateTime, formatINR } from "@/lib/utils";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

async function downloadCsv(path, filename) {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { Authorization: `Bearer ${tokenStore.get()}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(`Download failed: ${err.message}`);
  }
}

function CsvBtn({ path, filename, testid }) {
  return (
    <button
      onClick={() => downloadCsv(path, filename)}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#538135] hover:text-[#09582D] border border-[#538135]/30 hover:border-[#538135] rounded-md px-3 py-1.5 transition-colors"
    >
      <Download size={12} /> CSV
    </button>
  );
}

export default function AdminReportsPage() {
  const [revenue, setRevenue] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [top, setTop] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/admin/reports/revenue-timeseries?days=30"),
      api.get("/admin/reports/jobs-timeseries?days=30"),
      api.get("/admin/reports/top-users?limit=10"),
    ]).then(([r, j, t]) => { setRevenue(r.data); setJobs(j.data); setTop(t.data); })
      .catch(() => { setRevenue([]); setJobs([]); setTop([]); });
  }, []);

  if (!revenue || !jobs || !top) return <LoadingScreen />;

  const totalRevenue = revenue.reduce((s, d) => s + (d.amount_inr || 0), 0);
  const totalCredits = revenue.reduce((s, d) => s + (d.credits || 0), 0);
  const totalJobs = jobs.reduce((s, d) => s + (d.submitted || 0), 0);
  const totalCompleted = jobs.reduce((s, d) => s + (d.completed || 0), 0);
  const displayDate = (d) => d.slice(5); // MM-DD

  return (
    <div className="space-y-6" data-testid="admin-reports">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="sqit-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Revenue (30d)</div>
          <div className="mt-2 font-display text-2xl font-bold text-gray-900">{formatINR(totalRevenue)}</div>
        </div>
        <div className="sqit-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Credits sold (30d)</div>
          <div className="mt-2 font-display text-2xl font-bold text-gray-900">{totalCredits}</div>
        </div>
        <div className="sqit-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Jobs (30d)</div>
          <div className="mt-2 font-display text-2xl font-bold text-gray-900">{totalJobs}</div>
        </div>
        <div className="sqit-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Completed (30d)</div>
          <div className="mt-2 font-display text-2xl font-bold text-gray-900">{totalCompleted}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="sqit-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-gray-900">Revenue (last 30 days)</h3>
            <CsvBtn path="/admin/reports/revenue-timeseries.csv?days=30" filename="revenue_30d.csv" testid="csv-revenue" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenue.map((d) => ({ ...d, date: displayDate(d.date) }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                <Line type="monotone" dataKey="amount_inr" name="Revenue" stroke="#0B6936" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="sqit-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-gray-900">Jobs & completions (last 30 days)</h3>
            <CsvBtn path="/admin/reports/jobs-timeseries.csv?days=30" filename="jobs_30d.csv" testid="csv-jobs" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={jobs.map((d) => ({ ...d, date: displayDate(d.date) }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="submitted" name="Submitted" stroke="#09582D" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="completed" name="Completed" stroke="#0B6936" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="sqit-card overflow-hidden">
        <div className="p-6 pb-4 flex items-center justify-between">
          <h3 className="font-display font-semibold text-gray-900">Top users by job count</h3>
          <CsvBtn path="/admin/reports/top-users.csv?limit=100" filename="top_users.csv" testid="csv-top-users" />
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-y border-[#E5E7EB]">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Jobs</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Samples</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Credits used</th>
            </tr>
          </thead>
          <tbody>
            {top.length === 0 && (
              <tr><td colSpan={4} className="px-6 py-6 text-center text-gray-500">No jobs yet.</td></tr>
            )}
            {top.map((u) => (
              <tr key={u.user_id} className="border-b border-gray-100">
                <td className="px-6 py-3">
                  <div className="font-medium text-gray-900">{u.user_name}</div>
                  <div className="text-xs text-gray-500">{u.user_email}</div>
                </td>
                <td className="px-6 py-3">{u.job_count}</td>
                <td className="px-6 py-3">{u.samples}</td>
                <td className="px-6 py-3">{u.credits_used}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
